// A switch port's PoE, from its two sources (ZENITH.md §11.3, ADR-0264 as
// ruled: 1c, 2b, 3a), on the Ports table.
//
// **Two readings of one physical fact, side by side and never merged.** The
// gateway's `poe_draw_mw` comes with the page's live read (`ports/state`) and
// carries the time the switch wrote its report. The console's admin, oper and
// used power come from `POST /switches/{serial}/ports/poe:read`, which an
// operator asks for here, and carry the time of that read. Neither is derived
// from the other, compared with it into a word of agreement, or kept past the
// page that asked: the console reading lives in this page's state until the
// next read or the page goes.
//
// **An absence is said, and never as a value.** A figure a source did not give
// reads *not reported*, never 0 W. `off` on the console is a reported fact —
// the switch is not delivering power on the port — and is never an access
// point's liveness, which only §9.4's one definition says.
import { api } from "../../api/client";
import { oneConsole } from "../../api/oneConsole";
import { useMutation } from "../../api/mutation";
import type { components } from "../../api/schema";
import { ProblemBox } from "../../ui";
import type { Device } from "./commands";
import type { LivePort } from "./verdicts";

export type PoEReading = components["schemas"]["SwitchPortsPoEReading"];
type ConsolePort = components["schemas"]["SwitchPortPoEReading"];

const watts = (mw: number) => `${(mw / 1000).toFixed(1)} W`;
const clock = (d: Date) => d.toLocaleTimeString();

/** The gateway's draw for one port, in words: watts where the report carried
 * it, *not reported* where it did not. Exported so a test can pin that an
 * absent draw is never `0 W`. */
export function gatewayDrawWords(port: LivePort | undefined): string {
  if (port?.poe_draw_mw !== undefined) return watts(port.poe_draw_mw);
  const u = port?.unanswered.find((x) => x.field === "poe");
  if (u && u.reason !== "silent") return `not reported (${u.reason})`;
  return "not reported";
}

/** Whether the report carries no draw for the port and gives no reason for
 * it beyond saying nothing: the case a quiet table leaves empty (#1801). */
export function silentDraw(port: LivePort | undefined): boolean {
  if (port?.poe_draw_mw !== undefined) return false;
  const u = port?.unanswered.find((x) => x.field === "poe");
  return !u || u.reason === "silent";
}

/** The console's reading of one port, in words. A port the reading does not
 * list — one the switch does not power — is *not reported*. */
export function consolePoEWords(port: ConsolePort | undefined): string {
  if (!port) return "not reported";
  // Restates ADR-0264 decision 3a — Off is the port not delivering power, and never the device's online state.
  const oper = port.oper === "on" ? `on · ${watts(port.used_power_mw)}` : `off — not delivering power · ${watts(port.used_power_mw)}`;
  return port.admin === "disabled" ? `PoE disabled · ${oper}` : oper;
}

/** One port's PoE cell: the gateway's line, and the console's when one has
 * been read, each with its own source and time. */
export function PoECell({ status, port, reportedAt, name, reading, quiet = false }: {
  status: string;
  port?: LivePort;
  reportedAt?: Date;
  /** The port's name, which the console's reading is keyed by. */
  name: string;
  reading?: PoEReading;
  /** In a table that says once, above it, what an empty Power cell is (#1801):
   * a draw the report does not carry, with no reason beyond its silence and no
   * console reading beside it, is drawn as nothing rather than as the same
   * *not reported* on every row. Never as 0 W. */
  quiet?: boolean;
}) {
  const consolePort = reading?.ports.find((p) => p.port === name);
  const readAt = reading ? new Date(reading.read_at) : undefined;
  if (quiet && !reading && status !== "loading" && silentDraw(port)) return null;
  return (
    <>
      {/* Restates ADR-0264 decision 3a — the gateway's draw and the console's reading stand side by side, each with its source and time, never merged or compared. */}
      <div title="The port's draw from the switch's last state report, through the controller">
        Report: {status === "loading" ? <span className="muted">&hellip;</span>
          : <span className={port?.poe_draw_mw === undefined ? "muted" : undefined}>{gatewayDrawWords(port)}</span>}
        {status !== "loading" && port && reportedAt && <span className="muted"> · {clock(reportedAt)}</span>}
      </div>
      {reading && readAt && (
        <div title="Read over the switch's console with show power inline status">
          Console: <span className={consolePort ? undefined : "muted"}>{consolePoEWords(consolePort)}</span>
          <span className="muted"> · {clock(readAt)}</span>
        </div>
      )}
    </>
  );
}

/** The button that reads the switch's PoE over its console, and the refusal
 * when there is one. The reading goes to the caller, which draws it per port. */
export function PoERead({ device, reading, onRead }: { device: Device; reading?: PoEReading; onRead(r: PoEReading): void }) {
  const lane = useMutation();
  const read = async () => {
    const r = await lane.runWith(() => oneConsole(device.serial, "ports/poe:read", () => api.POST("/switches/{serial}/ports/poe:read", { params: { path: { serial: device.serial } } })));
    if (r.ok && r.data) onRead(r.data);
  };
  return (
    <>
      {/* Restates ADR-0264 decision 2b and ADR-0115 decision 4 — one show line, nothing kept, refused while a console, a read or a PoE cycle is open on the switch. */}
      <div className="actions">
        <button type="button" className="btn" onClick={() => void read()} disabled={lane.busy}
          title="Types show power inline status at the switch's console and shows each port's PoE beside the report's draw. Nothing is kept or sent to the switch. It is refused while a console, a read or a PoE cycle is open on the switch.">
          {lane.busy ? "Reading PoE from the console…" : reading ? "Read PoE from the console again" : "Read PoE from the console"}
        </button>
      </div>
      {lane.problem && <ProblemBox problem={lane.problem} onDismiss={lane.clear} />}
    </>
  );
}
